export const AGENT_COLORS = {
  Counselor: {
    signature: '#5B8FEA',
    light: '#8AB4F5',
    medium: '#6FA3EE',
    dark: '#4A7FE0',
    gradients: [
      { start: '#5B8FEA', end: '#A594E8', angle: 100 },
      { start: '#6FA3EE', end: '#8B6FD8', angle: 95 },
      { start: '#5B8FEA', end: '#9780E0', angle: 105 },
      { start: '#8AB4F5', end: '#A594E8', angle: 100 },
      { start: '#5B8FEA', end: '#8B6FD8', angle: 95 },
      { start: '#4A7FE0', end: '#9780E0', angle: 100 },
      { start: '#6FA3EE', end: '#A594E8', angle: 105 },
      { start: '#8AB4F5', end: '#8B6FD8', angle: 95 },
    ],
  },
  Action: {
    signature: '#D07AA5',
    light: '#D07AA5',
    medium: '#C05C8B',
    dark: '#8E3D68',
    gradients: [
      // Rose flowing to warm coral
      { start: '#C46894', end: '#E88A7E', angle: 100 },
      { start: '#B85A88', end: '#C95D52', angle: 95 },
      { start: '#D07AA5', end: '#D97166', angle: 105 },
      { start: '#AC4878', end: '#DD7D6F', angle: 100 },
      { start: '#C86898', end: '#E88A7E', angle: 95 },
      { start: '#BA5C8A', end: '#C95D52', angle: 100 },
      { start: '#D580A8', end: '#D97166', angle: 105 },
      { start: '#BE608C', end: '#DD7D6F', angle: 95 },
    ],
  },
  Coach: {
    signature: '#D84545',
    light: '#E85050',
    medium: '#D84545',
    dark: '#A03030',
    gradients: [
      { start: '#F96443', end: '#C93C3C', angle: 100 },
      { start: '#F05D3D', end: '#D84040', angle: 95 },
      { start: '#FF6D4D', end: '#BE3232', angle: 105 },
      { start: '#F86040', end: '#E04545', angle: 100 },
      { start: '#F96443', end: '#D43838', angle: 95 },
      { start: '#E85A3A', end: '#B82828', angle: 100 },
      { start: '#FA6845', end: '#C93C3C', angle: 105 },
      { start: '#F96443', end: '#D84040', angle: 95 },
    ],
  },
} as const;

export type AgentMode = keyof typeof AGENT_COLORS;

export interface GradientVariant {
  start: string;
  end: string;
  angle: number;
}

export interface ColorPalette {
  signature: string;
  light: string;
  medium: string;
  dark: string;
  gradients: readonly GradientVariant[];
}

export type AgentColorScheme = {
  [K in AgentMode]: ColorPalette;
};

/**
 * Normalize any category string to a valid AgentMode key
 */
export function normalizeCategory(category: string): AgentMode {
  // Normalize the category string to match one of the keys in AGENT_COLORS
  // This is a simple implementation based on the keys provided in the request
  // We can expand this logic if needed.
  if (category === 'Counselor') return 'Counselor';
  if (category === 'Action') return 'Action';
  if (category === 'Coach') return 'Coach';

  // Default fallback or more complex matching
  const lower = category.toLowerCase();
  if (
    lower.includes('counselor') ||
    lower.includes('inner') ||
    lower.includes('mastery')
  )
    return 'Counselor';
  if (
    lower.includes('action') ||
    lower.includes('leadership') ||
    lower.includes('gym')
  )
    return 'Action';
  if (lower.includes('coach') || lower.includes('performance')) return 'Coach';

  return 'Counselor'; // Default
}

export function getCategoryColor(category: string): string {
  const mode = normalizeCategory(category);
  console.log(AGENT_COLORS[mode].light, category);
  return `text-[${AGENT_COLORS[mode].signature}]`;
}

export function getCategoryStyle(category: string) {
  const mode = normalizeCategory(category);
  return {
    color: AGENT_COLORS[mode].signature,
  };
}

export function getCategoryGradients(
  category: string
): readonly GradientVariant[] {
  const mode = normalizeCategory(category);
  return AGENT_COLORS[mode].gradients;
}

export function getGradientForIndex(
  category: string,
  index: number
): GradientVariant {
  const gradients = getCategoryGradients(category);
  return gradients[index % gradients.length];
}

export function getCategoryPalette(category: string): ColorPalette {
  const mode = normalizeCategory(category);
  return AGENT_COLORS[mode];
}

export function formatGradient(
  gradient: GradientVariant,
  category?: string
): string {
  if (category && normalizeCategory(category) === 'Action') {
    return `linear-gradient(${gradient.angle}deg, ${gradient.start} 20%, ${gradient.end} 100%)`;
  }
  return `linear-gradient(${gradient.angle}deg, ${gradient.start}, ${gradient.end})`;
}

export function hexToRgba(hex: string, alpha: number = 1): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function formatGlassmorphicGradient(
  gradient: GradientVariant,
  category?: string,
  opacity: number = 0.75
): string {
  const startRgba = hexToRgba(gradient.start, opacity);
  const endRgba = hexToRgba(gradient.end, opacity);
  if (category && normalizeCategory(category) === 'Action') {
    return `linear-gradient(${gradient.angle}deg, ${startRgba} 20%, ${endRgba} 100%)`;
  }
  return `linear-gradient(${gradient.angle}deg, ${startRgba}, ${endRgba})`;
}

export function getGradientForIndexAsString(
  category: string,
  index: number,
  glassmorphic: boolean = true,
  opacity: number = 0.75
): string {
  const gradient = getGradientForIndex(category, index);
  if (glassmorphic) {
    return formatGlassmorphicGradient(gradient, category, opacity);
  }
  return formatGradient(gradient, category);
}

export function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

/**
 * Get the primary gradient string for a given tag/category (glassmorphic by default)
 * Returns a CSS linear-gradient string ready to use in style props
 */
export function getTagGradient(
  tag: string,
  glassmorphic: boolean = true,
  opacity: number = 0.75
): string {
  const palette = getCategoryPalette(tag);
  const gradient = palette.gradients[0];
  if (glassmorphic) {
    return formatGlassmorphicGradient(gradient, tag, opacity);
  }
  return formatGradient(gradient, tag);
}

/**
 * Get style object with background gradient for a given tag
 * Useful for applying to style props directly
 */
export function getTagBackgroundStyle(
  tag: string,
  glassmorphic: boolean = true,
  opacity: number = 0.75
): React.CSSProperties {
  return {
    background: getTagGradient(tag, glassmorphic, opacity),
  };
}

/**
 * Get the sidebar/navigation gradient (blue to coral)
 * Used for active navigation items
 */
export function getSidebarActiveGradient(opacity: number = 0.75): string {
  return `linear-gradient(135deg, rgba(96, 165, 250, ${opacity}) 0%, rgba(255, 107, 107, ${opacity}) 100%)`;
}

/**
 * Get the sidebar active gradient as a style object
 */
export function getSidebarActiveStyle(
  opacity: number = 0.75
): React.CSSProperties {
  return {
    background: getSidebarActiveGradient(opacity),
  };
}
